import React, { useRef, useEffect, useState } from 'react'
import { cn } from '../helpers/utils.js'

interface ScrollLoadVideoProps {
  src: string
  poster?: string
  className?: string
  videoClassName?: string
  autoplay?: boolean
  muted?: boolean
  loop?: boolean
  controls?: boolean
  playsInline?: boolean
  videoRef?: React.RefObject<HTMLVideoElement>
  intersectionObserverConfig?: {
    threshold?: number | number[]
    rootMargin?: string
  }
  [key: string]: any
}

const ScrollLoadVideo = ({
  src,
  poster,
  className = '',
  videoClassName = '',
  autoplay = true,
  muted = true,
  loop = true,
  controls = false,
  playsInline = true,
  videoRef,
  intersectionObserverConfig = {
    threshold: 0, // 当元素进入视窗时触发
    rootMargin: '200px', // 提前 200px 触发
  },
  ...attr
}: ScrollLoadVideoProps) => {
  const [shouldLoad, setShouldLoad] = useState(false)
  const internalVideoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // 跟踪视频是否已经播放完成（用于 loop=false 时只播放一次）
  const hasPlayedRef = useRef(false)

  const currentVideoRef = videoRef || internalVideoRef
  const { threshold, rootMargin } = intersectionObserverConfig

  // 监听视频播放结束事件
  useEffect(() => {
    const video = currentVideoRef.current
    if (!video || loop) return

    const handleEnded = () => {
      hasPlayedRef.current = true
    }

    video.addEventListener('ended', handleEnded)
    return () => {
      video.removeEventListener('ended', handleEnded)
    }
  }, [currentVideoRef, loop, shouldLoad])

  // IntersectionObserver 真正的懒加载 + 视窗播放控制
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // 第一次进入视窗时才开始加载视频
            if (!shouldLoad) {
              setShouldLoad(true)
            }

            // 如果允许自动播放且视频已加载
            if (currentVideoRef.current) {
              const video = currentVideoRef.current

              // 如果 loop=false 且视频已经播放过，不再重新播放
              if (!loop && hasPlayedRef.current) {
                return
              }

              video.muted = true
              video.currentTime = 0 // 每次进入视窗时从头播放
              video.play().catch((error: unknown) => {
                console.warn('视频自动播放失败:', error)
              })
            }
          } else if (currentVideoRef.current) {
            // 离开视窗时暂停视频
            currentVideoRef.current.pause()
          }
        })
      },
      {
        threshold: threshold,
        rootMargin: rootMargin,
      }
    )

    const currentElement = containerRef.current
    if (currentElement) {
      observer.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement)
      }
    }
  }, [currentVideoRef, loop, rootMargin, shouldLoad, threshold])

  return (
    <div ref={containerRef} className={cn('relative size-full', className)}>
      {/* 视频 */}
      <video
        ref={currentVideoRef}
        src={shouldLoad ? src : undefined} // 只有在需要时才设置 src
        poster={shouldLoad ? poster : undefined}
        className={cn('size-full object-cover', videoClassName)}
        loop={loop}
        muted={muted}
        controls={controls}
        playsInline={playsInline}
        preload="metadata" // 完全不预加载
        disablePictureInPicture
        // eslint-disable-next-line react/no-unknown-property
        webkit-playsinline
        autoPlay={autoplay}
        {...attr}
      />
    </div>
  )
}

export default ScrollLoadVideo
